'use client';

import { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { createClient, resetClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const FATAL_CODES = new Set([
  'refresh_token_already_used',
  'over_request_rate_limit',
  'invalid_refresh_token',
]);

const isFatalAuthError = (error: any): boolean => {
  if (!error) return false;
  const code = (error as any).code ?? '';
  const msg = (error.message ?? '').toLowerCase();
  return (
    FATAL_CODES.has(code) ||
    msg.includes('already used') ||
    msg.includes('rate limit') ||
    msg.includes('invalid refresh token')
  );
};

const clearSupabaseTokens = () => {
  try {
    // Remove all supabase auth keys from localStorage to stop refresh loops
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') || key.includes('supabase')) {
        localStorage.removeItem(key);
      }
    });
  } catch (_) {
    // localStorage not available (SSR)
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);
  const handlingFatal = useRef(false);

  const handleFatalError = async () => {
    if (handlingFatal.current) return;
    handlingFatal.current = true;
    clearSupabaseTokens();
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (_) {
      // ignore errors during cleanup signout
    }
    resetClient();
    setSession(null);
    setUser(null);
    setLoading(false);
  };

  useEffect(() => {
    const publicPaths = [
      '/',
      '/barber-landing',
      '/barber-landing-2',
      '/barber-landing-3',
      '/barber-landing-4',
      '/rep-landing',
      '/rep-signup',
      '/barber-signup',
      '/model-apply',
      '/job-interest',
      '/sign-up-login-screen',
      '/auth/callback',
      '/barber-set-password',
      '/employee-set-password',
      '/rep-set-password',
    ];
    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.some(
      (path) => currentPath === path || currentPath.startsWith(path + '/')
    );

    if (isPublicPage) {
      setLoading(false);
      return;
    }

    (async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error && isFatalAuthError(error)) {
        await handleFatalError();
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    })();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // TOKEN_REFRESHED with no session = refresh token was invalid/already used
      if (event === 'TOKEN_REFRESHED' && !session) {
        await handleFatalError();
        return;
      }
      // INITIAL_SESSION with no session after tokens exist = stale/invalid token
      if (event === 'INITIAL_SESSION' && !session) {
        // Check if there were tokens that should have produced a session
        const hadTokens = Object.keys(localStorage).some(
          (k) => k.startsWith('sb-') || k.includes('supabase')
        );
        if (hadTokens) {
          await handleFatalError();
          return;
        }
      }
      // SIGNED_OUT clears state cleanly
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    clearSupabaseTokens();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    supabase,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
